import { createAuthAxios, handleApiError } from "../../utils/apiUtils";
import { ROOMS_ENDPOINTS } from "../../config/apiConfig";

/**
 * Research Room Calendar Service
 * Handles all REST API calls for room calendar events, RSVP, and scheduling
 */
class RoomCalendarService {
  /**
   * Create a new calendar event
   * @param {String} roomId - Room ID
   * @param {Object} eventData - Event creation data
   * @returns {Promise<Object>} Created event
   */
  async createEvent(roomId, eventData) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }
      if (!eventData.title || !eventData.startDate || !eventData.endDate) {
        throw new Error("Title, start date, and end date are required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        ROOMS_ENDPOINTS.CALENDAR.CREATE(roomId),
        eventData
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get events for a room with optional filters
   * @param {String} roomId - Room ID
   * @param {Object} options - Query options (upcoming, startDate, endDate)
   * @returns {Promise<Object>} List of events
   */
  async getEvents(roomId, options = {}) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }

      const authAxios = createAuthAxios();
      const params = new URLSearchParams();

      if (options.upcoming) {
        params.append("upcoming", "true");
      }
      if (options.startDate) {
        params.append("startDate", options.startDate);
      }
      if (options.endDate) {
        params.append("endDate", options.endDate);
      }

      const url = `${ROOMS_ENDPOINTS.CALENDAR.LIST(roomId)}${
        params.toString() ? `?${params.toString()}` : ""
      }`;

      const response = await authAxios.get(url);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get a single event by ID
   * @param {String} roomId - Room ID
   * @param {String} eventId - Event ID
   * @returns {Promise<Object>} Event details
   */
  async getEventById(roomId, eventId) {
    try {
      if (!roomId || !eventId) {
        throw new Error("Room ID and Event ID are required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        ROOMS_ENDPOINTS.CALENDAR.DETAIL(roomId, eventId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Update an event (Creator or Leader only)
   * @param {String} roomId - Room ID
   * @param {String} eventId - Event ID
   * @param {Object} updates - Event updates
   * @returns {Promise<Object>} Updated event
   */
  async updateEvent(roomId, eventId, updates) {
    try {
      if (!roomId || !eventId) {
        throw new Error("Room ID and Event ID are required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.put(
        ROOMS_ENDPOINTS.CALENDAR.UPDATE(roomId, eventId),
        updates
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Delete/cancel an event (Creator or Leader only)
   * @param {String} roomId - Room ID
   * @param {String} eventId - Event ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteEvent(roomId, eventId) {
    try {
      if (!roomId || !eventId) {
        throw new Error("Room ID and Event ID are required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.delete(
        ROOMS_ENDPOINTS.CALENDAR.DELETE(roomId, eventId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Update user's RSVP for an event
   * @param {String} roomId - Room ID
   * @param {String} eventId - Event ID
   * @param {String} rsvp - RSVP status ('accepted', 'declined', 'tentative')
   * @returns {Promise<Object>} Updated RSVP data
   */
  async updateRSVP(roomId, eventId, rsvp) {
    try {
      if (!roomId || !eventId) {
        throw new Error("Room ID and Event ID are required");
      }
      if (!rsvp) {
        throw new Error("RSVP status is required");
      }

      const validRSVPs = ["accepted", "declined", "tentative"];
      if (!validRSVPs.includes(rsvp)) {
        throw new Error(`Invalid RSVP status: ${rsvp}`);
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.put(
        ROOMS_ENDPOINTS.CALENDAR.RSVP(roomId, eventId),
        { rsvp }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
}

export default new RoomCalendarService();
